/*
  LS combined country + language selector (snippets/ls-localization.liquid).
  The form itself is plain radios + a submit button and works without this
  file; this adds open/close, search filtering, the selected-row highlight
  and the currency note.
*/
if (!customElements.get('ls-localization')) {
  class LsLocalization extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.trigger = this.querySelector('.ls-loc__trigger');
      this.panel = this.querySelector('.ls-loc__panel');
      this.overlay = this.querySelector('.ls-loc__overlay');
      this.search = this.querySelector('.ls-loc__search-input');
      this.list = this.querySelector('.ls-loc__list');
      this.empty = this.querySelector('.ls-loc__empty');
      this.currency = this.querySelector('[data-ls-currency]');
      this.allItems = Array.prototype.slice.call(this.querySelectorAll('.ls-loc__group--all .ls-loc__item'));
      if (!this.trigger || !this.panel) return;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);

      // Button clicks are handled here, in the capture phase. Dawn's menu
      // drawer (assets/global.js, MenuDrawer) treats every <button> inside it
      // as a "close menu" button, so in the drawer the click must not reach
      // the button itself, or tapping the trigger closes the whole menu.
      // Stopping propagation does not cancel the default action, so the
      // Update button still submits the form.
      this.addEventListener(
        'click',
        (event) => {
          const button = event.target && event.target.closest ? event.target.closest('button') : null;
          if (!button || !this.contains(button)) return;
          if (this.dataset.position === 'drawer') event.stopPropagation();
          if (button === this.trigger) {
            event.preventDefault();
            if (this.isOpen()) {
              this.close(false);
            } else {
              this.open();
            }
          } else if (button.hasAttribute('data-ls-close')) {
            this.close(true);
          }
        },
        true
      );

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close(true));
      }

      this.addEventListener('change', (event) => {
        if (event.target && event.target.name === 'country_code') {
          this.select(event.target.value);
        }
      });

      if (this.search) {
        this.search.addEventListener('input', () => this.filter(this.search.value));
        this.search.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') event.preventDefault();
        });
      }

      // The menu drawer closes itself on Escape keyup; swallow that one
      // keyup when Escape was used to close this panel instead.
      this.addEventListener('keyup', (event) => {
        if (event.key === 'Escape' && this._swallowEscape) {
          event.stopPropagation();
          this._swallowEscape = false;
        }
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeyDown);
    }

    isOpen() {
      return this.trigger.getAttribute('aria-expanded') === 'true';
    }

    open() {
      this.panel.hidden = false;
      if (this.overlay) this.overlay.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
      // The open menu drawer has a transform and a filter, which would make
      // the "fixed" bottom sheet position itself inside the drawer instead
      // of the screen. ls-localization.css lifts both while the sheet is open.
      this.drawer = this.closest('.menu-drawer');
      if (this.drawer) this.drawer.classList.add('ls-loc-open');
      if (this.list) this.list.scrollTop = 0;
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeyDown);
      this.panel.focus({ preventScroll: true });
    }

    close(returnFocus) {
      this.panel.hidden = true;
      if (this.overlay) this.overlay.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      if (this.drawer) this.drawer.classList.remove('ls-loc-open');
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeyDown);
      if (this.search && this.search.value) {
        this.search.value = '';
        this.filter('');
      }
      if (returnFocus) this.trigger.focus({ preventScroll: true });
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.close(false);
    }

    onKeyDown(event) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      this._swallowEscape = true;
      this.close(true);
    }

    select(code) {
      let currency = null;
      this.querySelectorAll('.ls-loc__item').forEach((item) => {
        const match = item.dataset.code === code;
        item.classList.toggle('is-selected', match);
        if (match && !currency) currency = item.dataset.currency;
      });
      if (currency && this.currency) this.currency.textContent = currency;
    }

    filter(value) {
      const query = (value || '').trim().toLowerCase();
      let shown = 0;
      this.allItems.forEach((item) => {
        const haystack = item.dataset.search || '';
        const match = !query || haystack.indexOf(query) !== -1;
        item.hidden = !match;
        if (match) shown += 1;
      });
      this.classList.toggle('is-searching', query.length > 0);
      if (this.empty) this.empty.hidden = shown > 0;
      if (this.list) this.list.scrollTop = 0;
    }
  }

  customElements.define('ls-localization', LsLocalization);
}
